import { I18n } from '@calendly/i18n';
import { IconChevronLeft } from '@calendly/ui/components/glyphs/IconChevronLeft';
import { IconButton } from '@calendly/ui/components/icon-button';
import classNames from 'classnames';
import { ReactNode, useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
const surfaceCss = "skus672";
const headerBorderCss = "h1mv5c7d";
const headerCss = "h1gceu8g";
const bodyCss = "b1n26qw0";
const footerCss = "f1vopn0r";
export interface FullPageSurfaceProps {
  /** Where the back button navigates to. */
  returnTo: string;
  /** Override back-button behavior entirely (e.g. "back to menu" in small tier). */
  onBack?: () => void;
  /** Return false to block exit (unsaved changes). Called before navigation. Receives the destination
   * location when triggered by `history.block` (e.g. browser back, in-page push); called without args
   * when triggered by the back button. */
  onBeforeClose?: (location?: {
    pathname: string;
  }) => boolean;
  /** Content rendered to the right of the back button (title, actions, etc). */
  header?: ReactNode;
  /**
   * Whether to render a bottom border on the header bar.
   * Set to false when the content immediately below the header provides its own
   * visual separator (e.g. tabs that butt up against the header).
   * @default true
   */
  hasHeaderBorder?: boolean;
  /** Non-scrolling footer pinned to the bottom of the surface (e.g. primary actions on small tier). */
  footer?: ReactNode;
  children: ReactNode;
}

/**
 * Full-page surface frame. Renders a non-scrolling header bar with a back button
 * and a product-owned header slot, plus a scrolling body for page content.
 *
 * When `onBeforeClose` is provided, navigation is blocked via `history.block` whenever
 * the predicate returns false.
 */
export const FullPageSurface = ({
  returnTo,
  onBack,
  onBeforeClose,
  header,
  hasHeaderBorder = true,
  footer,
  children
}: FullPageSurfaceProps) => {
  const history = useHistory();
  const onBeforeCloseRef = useRef(onBeforeClose);
  onBeforeCloseRef.current = onBeforeClose;
  useEffect(() => {
    if (!onBeforeCloseRef.current) return undefined;
    const unblock = history.block((location: {
      pathname: string;
    }) => {
      if (onBeforeCloseRef.current && !onBeforeCloseRef.current(location)) {
        return I18n.t('publisher.app_shell.full_page_surface.unsaved_changes_prompt');
      }
      return undefined;
    });
    return unblock;
  }, [history]);
  const handleBack = () => {
    if (onBack) {
      onBack();
      return;
    }
    history.push(returnTo);
  };
  return <div className={surfaceCss} data-testid="full-page-surface">
      <header className={classNames(headerCss, {
      [headerBorderCss]: hasHeaderBorder
    })} data-testid="full-page-surface-header">
        <IconButton appearance="ghost" size="medium" icon={IconChevronLeft} aria-label={onBack ? I18n.t('publisher.app_shell.full_page_surface.back') : I18n.t('publisher.app_shell.full_page_surface.exit')} data-testid="full-page-surface-exit" onClick={handleBack} />
        {header}
      </header>
      <div className={bodyCss} data-testid="full-page-surface-body">
        {children}
      </div>
      {footer && <footer className={footerCss} data-testid="full-page-surface-footer">
          {footer}
        </footer>}
    </div>;
};

require("./FullPageSurface.wyw-in-js.css!=!../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./FullPageSurface.tsx");